import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { scheduleFilters } from "../shared/spectator";
import { LEGACY_TOURNAMENT_ID } from "../shared/tournament";
import { loadBundle } from "./i18n/load";
import {
  DEFAULT_ADMIN_TAB,
  defaultAdminTab,
  isAdminTab,
  parseAdminSearch,
} from "./lib/admin-search";
import { tournamentQuery } from "./lib/queries";
import { queryClient } from "./lib/query-client";
import { parseSuperSearch } from "./lib/super-search";
import { BoardPage, GamePublicPage, TeamPublicPage } from "./pages/BoardPage";
import { FrontPage } from "./pages/FrontPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OverlayPage } from "./pages/OverlayPage";
import { TournamentLayout } from "./pages/TournamentLayout";

const rootRoute = createRootRoute({ component: Outlet });

/**
 * The FRONT PAGE: every published tournament, grouped by when it is
 * (V2-ROADMAP.md Phase 2). It is the new QR landing and the installed app's
 * start page, so it lives in the main chunk with the board and the overlay.
 */
export const frontRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: FrontPage,
});

/**
 * The staff dictionary, fetched in PARALLEL with the surface's own chunk.
 *
 * Every gated route declares this, and nothing else may use a `staff` key:
 * `test/i18n.test.ts` enforces the second half. Splitting the dictionary this
 * way is what keeps the admin's several hundred strings, in two languages, out
 * of the chunk a spectator's QR code downloads.
 */
const staffMessages = () => loadBundle("staff");

// ---------- one tournament: everything under /t/$slug ----------

/**
 * The LAYOUT every tournament surface hangs off. Its component resolves the
 * slug against the registry once and provides the tournament to the tree
 * (`lib/tournament.tsx`); a slug nobody registered is a real "no such
 * tournament" page, in both languages, rather than an empty board.
 */
export const tournamentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug",
  component: TournamentLayout,
});

/** The board IS the tournament's index: `/t/<slug>` is what a QR code says. */
export const boardRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "/",
  component: BoardPage,
  validateSearch: scheduleFilters,
});

export const tournamentPlayersRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "players",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    team?: string;
    division?: string;
    sort?: "goals" | "assists" | "total" | "breaks";
  } => ({
    ...(typeof search["q"] === "string" && search["q"] ? { q: search["q"].slice(0, 120) } : {}),
    ...(typeof search["team"] === "string" && search["team"] ? { team: search["team"] } : {}),
    ...(typeof search["division"] === "string" && search["division"]
      ? { division: search["division"] }
      : {}),
    ...(search["sort"] === "goals" ||
    search["sort"] === "assists" ||
    search["sort"] === "total" ||
    search["sort"] === "breaks"
      ? { sort: search["sort"] }
      : {}),
  }),
  component: lazyRouteComponent(
    () => import("./pages/TournamentPlayersPage"),
    "TournamentPlayersPage",
  ),
});

/** A team's public page (board redesign, 2026-09-10): what the popup off a
 *  standings row used to show, with an address. `teams/<id>`, plural, so it
 *  never collides with the credentialed hub at `team/<club>`. */
export const teamPublicRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "teams/$teamId",
  component: TeamPublicPage,
});

/** A game's public page (board redesign, 2026-09-10): the point-by-point
 *  story that opened in a popup off a results row, with an address. */
export const gamePublicRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "games/$gameId",
  component: GamePublicPage,
});

// Overlay and board stay in the main chunk: OBS Browser Sources and QR-code
// landings should paint off one fetch. Admin surfaces and the bridge (which
// alone drags in obs-websocket-js) load their own chunks on demand.
export const overlayRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "overlay/$field",
  component: OverlayPage,
});

/** Bare `admin` lands on the default tab: the setup checklist for a draft
 *  (V2 Phase 4), the live fields otherwise. One registry read through the
 *  shared cache, which the layout underneath reuses. The search survives the
 *  hop, so a `?division=` pasted onto it still means something. */
export const adminHomeRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "admin",
  beforeLoad: async ({ params }) => {
    const status = await queryClient
      .ensureQueryData(tournamentQuery(params.slug))
      .then((t) => t.status)
      .catch(() => undefined);
    throw redirect({
      to: "/t/$slug/admin/$tab",
      params: { slug: params.slug, tab: defaultAdminTab(status) },
      search: true,
      replace: true,
    });
  },
  component: () => null,
});

/**
 * The organiser dashboard, one URL per tab (`/t/<slug>/admin/schedule`).
 *
 * The tab is a path segment and the rest of the view state (division, game,
 * view, filters) is typed search, validated once here. The tab could not be a
 * segment while the scorekeeper lived at `/admin/$field` - `isFieldId("bracket")`
 * is true - which is why the scorekeeper is under `admin/field/`.
 */
export const adminTabRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "admin/$tab",
  loader: staffMessages,
  validateSearch: parseAdminSearch,
  // An unknown segment lands on the default tab rather than on a blank admin.
  beforeLoad: ({ params }) => {
    if (!isAdminTab(params.tab)) {
      throw redirect({
        to: "/t/$slug/admin/$tab",
        params: { slug: params.slug, tab: DEFAULT_ADMIN_TAB },
        replace: true,
      });
    }
  },
  component: lazyRouteComponent(() => import("./pages/AdminHomePage"), "AdminHomePage"),
});

export const adminGameRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "admin/field/$field",
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/AdminGamePage"), "AdminGamePage"),
});

export const bridgeRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "bridge/$field",
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/BridgePage"), "BridgePage"),
});

/**
 * The TEAM HUB, opened by a participant link.
 *
 * A LAYOUT route with real children, which is the opposite of what `admin`
 * does — and the difference is not taste. `admin/$field` was the scorekeeper,
 * so an admin sub-view could never be a path segment; its tab therefore lives
 * in the query string. Nothing sits under `team`, so its views can be what
 * they read like, Back leaves a game detail rather than the hub, and a captain
 * can be sent a link to one view.
 *
 * Its own lazy chunk: a captain on field wifi has no reason to download the
 * admin dashboard, and the admin has no reason to carry this.
 */
/**
 * `team` alone RESOLVES which team (V2 Phase 5): a participant link's scope,
 * an account's club seat that plays in this event, or the superadmin's
 * `?team=`. It redirects to `team/$club` below, or says why it cannot.
 * `?view=` carries a legacy deep link's view across the hop.
 */
export const teamResolveRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "team",
  validateSearch: (search: Record<string, unknown>): { team?: string; view?: string } => ({
    ...(typeof search["team"] === "string" && search["team"] !== ""
      ? { team: search["team"] }
      : {}),
    ...(typeof search["view"] === "string" && search["view"] !== ""
      ? { view: search["view"] }
      : {}),
  }),
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/team/TeamPage"), "TeamResolve"),
});

/** The hub of ONE team of this tournament: `/t/<slug>/team/<club>`. The club
 *  id is the team id (D10), so a participant link and a club seat both name
 *  it, and the gate checks the credential against the path. */
export const teamRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "team/$club",
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/team/TeamPage"), "TeamPage"),
});

/** The default view: when do we play. */
export const teamScheduleRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./pages/team/SchedulePanel"), "TeamSchedulePanel"),
});

export const teamGamesRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "games",
  component: lazyRouteComponent(() => import("./pages/team/GamesPanel"), "TeamGamesPanel"),
});

/** One played game, point by point. A route and not a dialog: on a phone this
 *  is a screenful, and Back is what a reader presses to leave it. */
export const teamGameRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "games/$gameId",
  component: lazyRouteComponent(() => import("./pages/team/GameDetail"), "TeamGameDetail"),
});

export const teamStandingsRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "standings",
  component: lazyRouteComponent(() => import("./pages/team/StandingsPanel"), "TeamStandingsPanel"),
});

export const teamSpiritRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "spirit",
  component: lazyRouteComponent(() => import("./pages/team/SpiritPanel"), "TeamSpiritPanel"),
});

export const teamRosterRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "roster",
  component: lazyRouteComponent(() => import("./pages/team/RosterPanel"), "TeamRosterPanel"),
});

export const teamPickupsRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "pickups",
  component: lazyRouteComponent(() => import("./pages/team/PickupPanel"), "TeamPickupsPanel"),
});

/** The rules and the Регламент as hub views: the captains asked for them in
 *  the menu, reading in place - not as a jump to a second page. The bodies
 *  are the same components the standalone pages wrap. */
/** The hub's own rules view left on 2026-09-17 (the header carries the
 *  page): a link already sent lands on the global `/rules`. */
export const teamRulesRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "rules",
  beforeLoad: () => {
    throw redirect({ to: "/rules", replace: true });
  },
  component: () => null,
});

export const teamRegulationsRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "regulations",
  component: lazyRouteComponent(() => import("./pages/RegulationsPage"), "RegulationsBody"),
});

/**
 * `spirit` is one view of the hub now. Kept as a redirect because participant
 * links issued before the hub existed point here, and a grant token is shown
 * exactly once. The `#k=…` token is read and stripped BEFORE the first render
 * (`takeBootstrapToken` in `main.tsx`), so nothing depends on the fragment
 * surviving this hop.
 */
export const spiritRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "spirit",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/t/$slug/team",
      params: { slug: params.slug },
      search: (prev: Record<string, unknown>) => ({ ...prev, view: "spirit" }),
      replace: true,
    });
  },
  component: () => null,
});

/** The Регламент reader: public, lazy - loads only when somebody opens it. */
export const regulationsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "regulations",
  component: lazyRouteComponent(() => import("./pages/RegulationsPage"), "RegulationsPage"),
});

/** `board` under a tournament is the board itself; the hash rides along. */
export const boardAliasRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "board",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      to: "/t/$slug",
      params: { slug: params.slug },
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

// ---------- the legacy addresses: aliases for the event `main` ----------

/**
 * Every URL this app minted before the registry existed — QR codes, OBS
 * Browser Sources, installed boards, staff links in Telegram — points at
 * `/admin`, `/overlay/1`, `/team` and friends, and every one of them was
 * minted for the legacy event (V2-RESEARCH.md D8). So each of those paths
 * redirects to the same page under that event's registered slug, with the
 * hash AND the query preserved: a `#k=` link keeps its token (already consumed
 * into memory by then, but the URL stays honest), a `#next` deep link keeps
 * its view, and the showcase's `?preview=1` still reaches the overlay.
 *
 * The slug is one registry read through the shared query cache. When there is
 * none — a deployment whose owner has not yet registered the existing event,
 * or no network at all — the front page is the only honest place to land.
 */
async function legacySlug(): Promise<string> {
  try {
    return (await queryClient.ensureQueryData(tournamentQuery(LEGACY_TOURNAMENT_ID))).slug;
  } catch {
    throw redirect({ to: "/", replace: true });
  }
}

export const legacyBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/board",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug",
      params: { slug },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyOverlayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overlay/$field",
  beforeLoad: async ({ params, location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/overlay/$field",
      params: { slug, field: params.field },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/admin/$tab",
      params: { slug, tab: DEFAULT_ADMIN_TAB },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyAdminTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/$tab",
  beforeLoad: async ({ params, location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/admin/$tab",
      params: { slug, tab: params.tab },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyAdminGameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/field/$field",
  beforeLoad: async ({ params, location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/admin/field/$field",
      params: { slug, field: params.field },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyBridgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bridge/$field",
  beforeLoad: async ({ params, location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/bridge/$field",
      params: { slug, field: params.field },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamGamesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/games",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "games" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamGameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/games/$gameId",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "games" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamStandingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/standings",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "standings" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamSpiritRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/spirit",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "spirit" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamRosterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/roster",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "roster" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyTeamRulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/rules",
  // The rules are the platform's page, not the hub's (2026-09-17).
  beforeLoad: () => {
    throw redirect({ to: "/rules", replace: true });
  },
  component: () => null,
});

export const legacyTeamRegulationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/regulations",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "regulations" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacySpiritRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/spirit",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/team",
      params: { slug },
      search: (prev) => ({ ...prev, view: "spirit" }),
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

export const legacyRegulationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regulations",
  beforeLoad: async ({ location }) => {
    const slug = await legacySlug();
    throw redirect({
      to: "/t/$slug/regulations",
      params: { slug },
      search: true,
      hash: location.hash,
      replace: true,
    });
  },
  component: () => null,
});

// ---------- the platform ----------

/**
 * THE CLUB PAGE (V2 Phase 5, D23): `/team/<club slug>`, the club's own record
 * across events - library roster, crest, people, the tournaments it has
 * entered. Gated by an account with a seat on the club, or the super. Its
 * own lazy chunk with staff strings. The legacy `/team/<view>` redirects
 * above are STATIC segments and win over this dynamic one, which is why
 * `CLUB_RESERVED_SLUGS` keeps those words out of club slugs.
 */
export const clubRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team/$club",
  validateSearch: (search: Record<string, unknown>): { view?: "squad" | "settings" } => ({
    ...(search["view"] === "squad" || search["view"] === "settings"
      ? { view: search["view"] }
      : {}),
  }),
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/club/ClubPage"), "ClubPage"),
});

/** The rules index: a public, lazy page of styled links into urules.org's
 *  Ukrainian WFDF rules. Not a tournament's: the sport's. */
export const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rules",
  component: lazyRouteComponent(() => import("./pages/RulesPage"), "RulesPage"),
});

/**
 * THE TEAMS (board redesign, round 15): every club on the platform, with a
 * search, and a club's public profile - where it played, how it finished.
 * Public, lazy, core strings only. `/team/<club>` (singular) stays the
 * club's own gated page.
 */
export const pickupsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: "/pickups",
  component: lazyRouteComponent(() => import("./pages/PickupsPage"), "PickupsPage"),
});

/** The people. `?club=<id>` narrows the list to one club's seats, so a
 *  filtered list can be sent as a link (2026-09-11). */
export const playersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/players",
  validateSearch: (search: Record<string, unknown>): { club?: string } => ({
    ...(typeof search["club"] === "string" && search["club"] !== ""
      ? { club: search["club"] }
      : {}),
  }),
  component: lazyRouteComponent(() => import("./pages/PlayersPage"), "PlayersPage"),
});
export const playerProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/players/$player",
  component: lazyRouteComponent(() => import("./pages/PlayersPage"), "PlayerProfilePage"),
});

export const teamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams",
  component: lazyRouteComponent(() => import("./pages/TeamsPage"), "TeamsPage"),
});
export const teamProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/$club",
  component: lazyRouteComponent(() => import("./pages/TeamsPage"), "TeamProfilePage"),
});

/**
 * Sign in with an ACCOUNT (V2 Phase 3): email + password, on its own cookie.
 * `?next=` is the gate that sent the person here, and only ever a path on
 * this origin: an absolute URL in it would be an open redirect.
 */
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  loader: staffMessages,
  validateSearch: (search: Record<string, unknown>): { next?: string; error?: string } => {
    const next = search["next"];
    const error = search["error"];
    return {
      ...(typeof next === "string" && next.startsWith("/") && !next.startsWith("//")
        ? { next }
        : {}),
      // The one word the Telegram callback may leave here (V3 Phase 1).
      ...(error === "telegram" ? { error } : {}),
    };
  },
  component: lazyRouteComponent(() => import("./pages/LoginPage"), "LoginPage"),
});

/**
 * The account's own record (V3 Phase 1, D36): name, language, Telegram,
 * password, deletion. Gated by an account session; a lazy staff chunk.
 * `?telegram=` is the one word the OIDC callback leaves after a connect.
 */
export const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  loader: staffMessages,
  validateSearch: (search: Record<string, unknown>): { telegram?: string; view?: "settings" } => {
    const telegram = search["telegram"];
    return {
      ...(typeof telegram === "string" && /^[a-z-]{1,32}$/.test(telegram) ? { telegram } : {}),
      ...(search["view"] === "settings" ? { view: "settings" as const } : {}),
    };
  },
  component: lazyRouteComponent(() => import("./pages/AccountPage"), "AccountPage"),
});

/**
 * The page an invitation opens (`/invite#k=…`). The token is lifted off the
 * fragment before the first render and kept in memory only: `main.tsx`
 * routes a `#k=` on THIS path to the invite slot rather than the session
 * exchange (`lib/bootstrap-token.ts`).
 */
export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/InvitePage"), "InvitePage"),
});

/**
 * The page a squad's pickup invite link opens (`/pickup-invite#k=…`, or the
 * Mini App's `startapp=pk…`, 2026-09-15). Like `/invite`, the token is lifted
 * before the first render and kept in memory only (`lib/bootstrap-token.ts`).
 * Core strings only: a player arriving from a chat downloads no staff bundle.
 */
export const pickupInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pickup-invite",
  component: lazyRouteComponent(() => import("./pages/PickupInvitePage"), "PickupInvitePage"),
});

/**
 * Support chats (2026-09-16, `shared/support.ts`): the agent's inbox or a
 * person's door at `/support`, one chat at `/support/<id>`. A lazy staff
 * chunk; the chat's link token (`#k=`) is lifted before the first render
 * like a grant's (`lib/bootstrap-token.ts`) and spent on the chat cookie.
 */
export const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/support/SupportPage"), "SupportPage"),
});

export const supportChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support/$chat",
  loader: staffMessages,
  component: lazyRouteComponent(() => import("./pages/support/SupportChatPage"), "SupportChatPage"),
});

/**
 * The platform surface. Its own lazy chunk on purpose (AUTH.md §6): an
 * organiser must never download it, and it must not be reachable by poking at
 * the admin UI.
 */
export const superRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/super",
  loader: staffMessages,
  // Which view is open lives in the URL, like /admin's tab. A search param and
  // not a path segment, for the same reason: nothing else should have to know
  // what /super's sub-views are called.
  validateSearch: parseSuperSearch,
  component: lazyRouteComponent(() => import("./pages/SuperPage"), "SuperPage"),
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    frontRoute,
    tournamentRoute.addChildren([
      boardRoute,
      tournamentPlayersRoute,
      pickupsRoute,
      teamPublicRoute,
      gamePublicRoute,
      overlayRoute,
      adminHomeRoute,
      adminTabRoute,
      adminGameRoute,
      bridgeRoute,
      teamResolveRoute,
      teamRoute.addChildren([
        teamScheduleRoute,
        teamGamesRoute,
        teamGameRoute,
        teamStandingsRoute,
        teamSpiritRoute,
        teamRosterRoute,
        teamPickupsRoute,
        teamRulesRoute,
        teamRegulationsRoute,
      ]),
      spiritRoute,
      regulationsRoute,
      boardAliasRoute,
    ]),
    legacyBoardRoute,
    legacyOverlayRoute,
    legacyAdminRoute,
    legacyAdminTabRoute,
    legacyAdminGameRoute,
    legacyBridgeRoute,
    legacyTeamRoute,
    legacyTeamGamesRoute,
    legacyTeamGameRoute,
    legacyTeamStandingsRoute,
    legacyTeamSpiritRoute,
    legacyTeamRosterRoute,
    legacyTeamRulesRoute,
    legacyTeamRegulationsRoute,
    legacySpiritRoute,
    legacyRegulationsRoute,
    rulesRoute,
    playersRoute,
    playerProfileRoute,
    teamsRoute,
    teamProfileRoute,
    clubRoute,
    loginRoute,
    accountRoute,
    inviteRoute,
    pickupInviteRoute,
    supportRoute,
    supportChatRoute,
    superRoute,
  ]),
  // A path nobody registered gets the same door as every other refusal,
  // not the router's own two words on a light ground.
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
